import { DomainError } from '../errors.js';

/**
 * Yönetici oturumunun süreleri (ADR 0039 §5): boşta 15 dakika, mutlak 8 saat; "cihazı hatırla"
 * yok. Emsal: NIST SP 800-63B-4 AAL3 (15 dakika hareketsizlik, 12 saat yeniden doğrulama),
 * GitLab "Admin Mode" (6 saat).
 *
 * Ayar yalnız **kısaltır** (ADR 0039 §5 "kısaltılabilir. Kapatılamaz"): boşta 5–15 dakika, mutlak
 * 1–8 saat. ADR'deki "5–30" ve "1–12" aralıkları varsayılanı uzatmaya izin veriyordu; "yalnız
 * kısaltır" kuralıyla çeliştiği için daha sıkı olan seçildi (ADR 0039 Ek, A1a).
 */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;

export interface AdminSessionPolicy {
  /** Bu kadar işlem yapılmazsa oturum düşer. */
  idleMs: number;
  /** Açılıştan itibaren en uzun ömür. */
  absoluteMs: number;
}

export const ADMIN_SESSION_DEFAULTS: AdminSessionPolicy = Object.freeze({
  idleMs: 15 * MINUTE,
  absoluteMs: 8 * HOUR,
});

const IDLE_MINUTES = { min: 5, max: 15 } as const;
const ABSOLUTE_HOURS = { min: 1, max: 8 } as const;

function inRange(value: number, range: { min: number; max: number }): boolean {
  return Number.isInteger(value) && value >= range.min && value <= range.max;
}

/** Ortam ayarından politika; verilmeyen değer varsayılandır, aralık dışı değer reddedilir. */
export function parseSessionPolicy(input: {
  idleMinutes?: number | undefined;
  absoluteHours?: number | undefined;
}): AdminSessionPolicy {
  const { idleMinutes, absoluteHours } = input;
  if (idleMinutes !== undefined && !inRange(idleMinutes, IDLE_MINUTES)) {
    throw new DomainError('INVALID_SESSION_POLICY', `idle ${idleMinutes}`);
  }
  if (absoluteHours !== undefined && !inRange(absoluteHours, ABSOLUTE_HOURS)) {
    throw new DomainError('INVALID_SESSION_POLICY', `absolute ${absoluteHours}`);
  }
  return {
    idleMs: idleMinutes === undefined ? ADMIN_SESSION_DEFAULTS.idleMs : idleMinutes * MINUTE,
    absoluteMs:
      absoluteHours === undefined ? ADMIN_SESSION_DEFAULTS.absoluteMs : absoluteHours * HOUR,
  };
}

/**
 * Oturum `now` anında geçerli mi: açılıştan beri mutlak süre, son işlemden beri boşta süresi
 * dolmamış olmalı. Son işlem gelecekteyse (saat geri gitmiş) geçersiz sayılır.
 */
export function sessionValid(
  session: { createdAt: string; lastSeenAt: string },
  now: string,
  policy: AdminSessionPolicy,
): boolean {
  const at = Date.parse(now);
  const created = Date.parse(session.createdAt);
  const lastSeen = Date.parse(session.lastSeenAt);
  if ([at, created, lastSeen].some(Number.isNaN)) return false;
  if (lastSeen > at || created > at) return false;
  return at - created < policy.absoluteMs && at - lastSeen < policy.idleMs;
}
